/**
 * 69.31 (DECISIONS 269 (1), (4)): the dumps' arms in play, and the tank's ammunition door — today's rearm (core/munitions.ts
 * `stepRearm`) drawn. With the Lab's *Dump arm* row (?rearmarm=1) and Ammunition at the dumps, human scale:
 *   - **An arm at every dump** (a base crate's rounds, a FOB's: the stacks rendering/ammoBoxes.ts lays beside it), on its skid
 *     by the crate beside the stacks' first row, on the drawn ground, in the dump's frame (it scales and hides with it). A parked supply truck's bed is a dump too,
 *     but its arm (the sheet's C) was not picked: nothing is drawn there.
 *   - **Which vehicle it loads**: the one the sim's own rule (`rearmDumpOf`) rearms from that dump — the first in the state's
 *     order when several do. Its round's type is the store it takes next (the crate in the jaws that type's).
 *   - **The cycle** follows the sim's progress toward the round (the vehicle's `rearm`, stepped on the rearm's beat, read
 *     forward by the match's time between beats), a crate's worth of rounds a cycle (ammoBoxes' `per`: the tank's two, as the
 *     stacks show them): pick at the top crate of the type, swing, set at the vehicle's loading
 *     point — the tank's open ammunition door (rendering/tankLooks.ts TANK_AMMO_DOOR), else the top of the vehicle's hull
 *     at its middle. Out of reach, the jaws go as far toward it as the booms do. Served or not, the arm eases between its
 *     rest and the cycle.
 *   - **The door** opens while its tank is served through it (its bustle's back toward the arm; else the crate is set on the
 *     bustle's roof, the jaws coming down from above) and shuts after.
 * Draws only: no state, no sim. Each arm five draws, and the crate in its jaws one (hidden past REARM_ARMS_PEOPLE).
 */
import * as THREE from 'three';
import type { AmmoType, GameState, StructureState } from '../types';
import { TEAMS, MUNITIONS, PHYS_DT } from '../constants';
import { rearmDumpOf } from '../core/munitions';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { structureTeam } from '../core/body';
import { buildRearmArm, poseRearmArm, rearmArmCycle, swingBetween, REARM_ARM, type RearmArm } from '../rendering/rearmArm';
import { topCrateAt, AMMO_CRATES, type AmmoDump } from '../rendering/ammoBoxes';
import { TANK_AMMO_DOOR } from '../rendering/tankLooks';
import type { VehicleModel } from '../rendering/vehicleModels';
import { hullName } from '../rendering/orbitShips';
import { unitPaletteFor } from '../rendering/unitPalette';

/** Past this many people from the camera an arm is hidden. */
export const REARM_ARMS_PEOPLE = 250;
/** Seconds between looks for new dumps (and the arms' distance from the camera). */
const SCAN_S = 0.5;
/** The arm's ease between its rest and the cycle, and the door's, a second. */
const ARM_RATE = 1.5, DOOR_RATE = 1.8;
/**
 * Where the arm's skid stands in the dump's frame (metres: x along its rows, z across them), clear of the structure's look and
 * the first stack: a base crate's, by the crate beside the stacks' first row (the dump's frame starts 0.95 m off the crate's
 * middle); a FOB's, off the FOB's rear corner beside its stacks (its dump turned behind it; the FOB's look 4.6 m wide).
 */
const ARM_AT = { supply: { x: -0.55, z: -1.3 }, fob: { x: 0.6, z: -1.6 } } as const;

interface Arm { arm: RearmArm; dump: AmmoDump; served: number | null; w: number; type: AmmoType; at: THREE.Vector3; pick: THREE.Vector3; set: THREE.Vector3 }

export class RearmArms {
  private arms = new Map<number, Arm>();
  /** the vehicles served last frame: read again while their `rearm` is between rounds (the beat a round is taken it is gone) */
  private served = new Set<number>();
  /** each served vehicle's `rearm` as last read and the match's time then (its progress read forward between beats) */
  private seen = new Map<number, { r: number; at: number }>();
  /** each tank's door, 0 shut … 1 open */
  private doors = new Map<number, number>();
  private loadPoints = new WeakMap<VehicleModel, THREE.Object3D>();
  private scanIn = 0;

  constructor(private structureGroup: (id: number) => THREE.Object3D | undefined, private vehicleModel: (id: number) => VehicleModel | undefined, private groundAt: (planetIndex: number, dir: { x: number; y: number; z: number }) => number) {}

  update(state: GameState, actionTick: number, dt: number, camera: THREE.Vector3): void {
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, now = actionTick * PHYS_DT;
    if ((this.scanIn -= dt) <= 0) { this.scanIn = SCAN_S; this.scan(state, camera, person); }
    // which vehicle each dump serves (the sim's rule), and how far through its round
    const serving = new Map<number, { v: StructureState; type: AmmoType; k: number }>(), served = new Set<number>();
    for (const s of state.structures) {
      if (!s.drive || (s.rearm === undefined && !this.served.has(s.id))) continue;
      const f = rearmDumpOf(state.structures, s, state, ps, person);
      if (!f || f.bed || !this.arms.has(f.dump.id)) { this.seen.delete(s.id); continue; }
      served.add(s.id);
      const r = s.rearm ?? 0, was = this.seen.get(s.id);
      if (!was || was.r !== r) this.seen.set(s.id, { r, at: now });
      // (a crate holds `per` rounds: its cycle spans them, from the round its predecessor's last left the stack — the dump's
      // count of the type a multiple of `per` — so the stack shows one fewer as the jaws take it)
      const seen = this.seen.get(s.id)!, type = f.store.type, per = AMMO_CRATES[type].per, left = f.dump.supply!.ammo![type] ?? 0;
      const j = (per - (left % per)) % per, round = Math.min(1, (seen.r + (now - seen.at)) / MUNITIONS.pace[type]);
      if (!serving.has(f.dump.id)) serving.set(f.dump.id, { v: s, type, k: Math.min(1, (j + round) / per) });
    }
    for (const id of this.served) if (!served.has(id)) this.seen.delete(id);
    this.served = served;
    // the arms
    const doorsOpen = new Set<number>();
    for (const [id, a] of this.arms) {
      const job = serving.get(id), m = job && this.vehicleModel(job.v.id);
      const want = job && m ? 1 : 0;
      if (want === 0 && a.w === 0) continue;
      a.w = Math.max(0, Math.min(1, a.w + Math.sign(want - a.w) * ARM_RATE * dt));
      if (job && m) {
        a.served = job.v.id; a.type = job.type;
        const top = topCrateAt(a.dump, job.type);
        a.arm.root.updateMatrix();
        if (top) a.pick.set(top.x, top.y, top.z).applyMatrix4(_inv.copy(a.arm.root.matrix).invert());   // (the dump's frame to the arm's)
        m.root.updateMatrixWorld(true);
        a.arm.root.updateMatrixWorld(true);
        const viaDoor = this.setPoint(m, a.arm.root, a.set);
        if (viaDoor) doorsOpen.add(job.v.id);
        const c = rearmArmCycle(job.k, a.pick, a.set, a.at);
        poseRearmArm(a.arm, swingBetween(REARM_ARM.rest, a.at, a.w, 0, _e), c.carry && a.w > 0.95 ? job.type : null);
      } else {
        a.served = null;
        poseRearmArm(a.arm, swingBetween(REARM_ARM.rest, a.at, a.w, 0, _e), null);
      }
    }
    // the doors: open while served, shut after
    for (const id of doorsOpen) if (!this.doors.has(id)) this.doors.set(id, 0);
    for (const [id, k] of this.doors) {
      const door = this.vehicleModel(id)?.tank?.door;
      const next = Math.max(0, Math.min(1, k + (doorsOpen.has(id) ? 1 : -1) * DOOR_RATE * dt));
      if (door) { door.rotation.x = TANK_AMMO_DOOR.open * next * next * (3 - 2 * next); door.updateMatrixWorld(true); }
      if (next === 0 && !doorsOpen.has(id)) this.doors.delete(id); else this.doors.set(id, next);
    }
  }

  /** Each standing dump's arm made (and kept on the dump's group as drawn now), each hidden past REARM_ARMS_PEOPLE. */
  private scan(state: GameState, camera: THREE.Vector3, person: number): void {
    const live = new Set<number>();
    for (const s of state.structures) {
      if ((s.type !== 'supply' && s.type !== 'fob') || !s.supply?.ammo) continue;
      const g = this.structureGroup(s.id);
      const dumpGroup = g?.children.find(c => c.userData.dump) as THREE.Group | undefined;
      if (!dumpGroup) continue;
      live.add(s.id);
      const dump = dumpGroup.userData.dump as AmmoDump;
      let a = this.arms.get(s.id);
      if (!a || a.dump !== dump) {
        if (a) a.arm.root.removeFromParent();
        const team = TEAMS[(s.supply.teamIndex ?? structureTeam(state, s) ?? 0) % TEAMS.length];
        const arm = buildRearmArm(unitPaletteFor(team), { name: hullName(team.name), mark: 0 });
        // (as near the middle of the rearm's reach as the structure and the stacks leave room)
        const at = ARM_AT[s.type];
        arm.root.position.set(at.x, 0, at.z);
        dumpGroup.add(arm.root);
        a = { arm, dump, served: null, w: 0, type: 'tankHe', at: REARM_ARM.rest.clone(), pick: new THREE.Vector3(), set: new THREE.Vector3() };
        this.arms.set(s.id, a);
      }
      a.arm.root.visible = a.arm.root.getWorldPosition(_e).distanceTo(camera) < REARM_ARMS_PEOPLE * person;
      // its skid on the drawn ground (the dump's pad levels only the crate's own footprint; a crater moves it)
      const planet = state.planets[s.planetIndex];
      if (planet && a.arm.root.visible) {
        a.arm.root.position.y = 0;
        a.arm.root.updateMatrixWorld(true);
        const at = a.arm.root.getWorldPosition(_e), P = _n.set(planet.position.x, planet.position.y, planet.position.z), d = at.sub(P).normalize();
        const r = this.groundAt(s.planetIndex, { x: d.x, y: d.y, z: d.z });
        a.arm.root.position.y = dumpGroup.worldToLocal(d.multiplyScalar(r).add(P)).y;
      }
    }
    for (const [id, a] of this.arms) if (!live.has(id)) { a.arm.root.removeFromParent(); this.arms.delete(id); }
  }

  /**
   * Where the arm sets the crate on `m`, in the arm's frame (into `out`): the tank's open door while its bustle's back faces the
   * arm (returns true: the door opens), else the bustle's roof (the jaws come down from above, not through the turret); any
   * other vehicle the top of its hull at its middle.
   */
  private setPoint(m: VehicleModel, arm: THREE.Object3D, out: THREE.Vector3): boolean {
    const t = m.tank, turret = m.turret;
    if (!t?.loadPoint || !turret) { arm.worldToLocal(this.loadPoint(m).getWorldPosition(out)); return false; }
    t.loadPoint.getWorldPosition(out);
    const back = _n.set(0, 0, -1).transformDirection(turret.matrixWorld), toArm = arm.getWorldPosition(_e).sub(out);
    if (back.dot(toArm) > 0) { arm.worldToLocal(out); return true; }
    arm.worldToLocal(turret.localToWorld(out.copy(BUSTLE_ROOF)));
    return false;
  }

  /** Where a vehicle's rounds go in, when not the tank's: the top of its hull at its middle (found once a model). */
  private loadPoint(m: VehicleModel): THREE.Object3D {
    if (m.tank?.loadPoint) return m.tank.loadPoint;
    let o = this.loadPoints.get(m);
    if (!o) {
      // (the hull's box in the model's own frame: each visible part's box carried into it)
      m.root.updateMatrixWorld(true);
      const inv = new THREE.Matrix4().copy(m.root.matrixWorld).invert(), box = new THREE.Box3(), part = new THREE.Box3(), to = new THREE.Matrix4();
      m.full.traverseVisible(q => {
        const mesh = q as THREE.Mesh;
        if (!mesh.isMesh || (mesh as unknown as THREE.InstancedMesh).isInstancedMesh) return;
        if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
        box.union(part.copy(mesh.geometry.boundingBox!).applyMatrix4(to.multiplyMatrices(inv, mesh.matrixWorld)));
      });
      const c = box.getCenter(new THREE.Vector3());
      o = new THREE.Object3D(); o.position.set(c.x, box.max.y, c.z); m.root.add(o);
      this.loadPoints.set(m, o);
    }
    return o;
  }

  dispose(): void {
    for (const a of this.arms.values()) { a.arm.root.removeFromParent(); a.arm.root.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh && m.name === 'rearm arm part') m.geometry.dispose(); }); }
    this.arms.clear(); this.doors.clear(); this.served.clear(); this.seen.clear();
  }
}
const _e = new THREE.Vector3(), _n = new THREE.Vector3(), _inv = new THREE.Matrix4();
/** The tank's bustle's roof, over its middle (the turret's frame: rendering/tankLooks.ts, turret B). */
const BUSTLE_ROOF = new THREE.Vector3(0, 0.56, -1.22);
